import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";

import {
  getApiErrorMessage,
  listDepartments,
  updateCompanyEvent,
} from "../../services/adminCompanyService.js";

const PERIODS_URL = "https://cloud-master.perufedup.com/public/catalogue/MjA=";

const toDateInput = (value) => (value ? String(value).slice(0, 10) : "");

const buildForm = (event) => ({
  name: event?.name || "",
  periodo: event?.periodo != null ? String(event.periodo) : "",
  startdate: toDateInput(event?.startdate),
  enddate: toDateInput(event?.enddate),
  place: event?.place || "",
  iddepartamento:
    event?.iddepartamento != null ? String(event.iddepartamento) : "",
  state: event?.state != null ? String(event.state) : "1",
});

const EventEditModal = ({ event, idcompany, onClose, onSaved }) => {
  const [form, setForm] = useState(() => buildForm(event));
  const [periods, setPeriods] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(event?.image_path || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(buildForm(event));
    setImageFile(null);
    setImagePreview(event?.image_path || "");
  }, [event]);

  useEffect(() => {
    let active = true;

    Promise.all([
      axios.get(PERIODS_URL, { withCredentials: false }),
      listDepartments(),
    ])
      .then(([periodResponse, departmentItems]) => {
        if (!active) return;

        const periodItems = Array.isArray(periodResponse.data)
          ? periodResponse.data
          : [];
        setPeriods(
          periodItems
            .filter((item) => item?.name != null)
            .map((item) => String(item.name))
            .sort((a, b) => b.localeCompare(a, undefined, { numeric: true }))
        );
        setDepartments(departmentItems);
      })
      .catch((error) => {
        console.error("Error al cargar catálogos del evento", error);
        toast.error("No se pudieron cargar los catálogos del formulario.");
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    return () => {
      if (imagePreview?.startsWith("blob:")) URL.revokeObjectURL(imagePreview);
    };
  }, [imagePreview]);

  const handleChange = (changeEvent) => {
    const { name, value } = changeEvent.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleImageChange = (changeEvent) => {
    const file = changeEvent.target.files?.[0] || null;
    setImageFile(file);
    setImagePreview(file ? URL.createObjectURL(file) : event?.image_path || "");
  };

  const handleSubmit = async (submitEvent) => {
    submitEvent.preventDefault();

    if (!form.name.trim() || !form.periodo || !form.place.trim()) {
      toast.error("Nombre, periodo y lugar son obligatorios.");
      return;
    }

    setSaving(true);
    try {
      const payload = new FormData();
      Object.entries({
        ...form,
        name: form.name.trim(),
        place: form.place.trim(),
        idcompany,
      }).forEach(([key, value]) => payload.append(key, value ?? ""));
      if (imageFile) payload.append("image", imageFile);

      await updateCompanyEvent({
        idchampionship: event.idchampionship,
        payload,
      });
      toast.success("Evento actualizado correctamente.");
      await onSaved?.();
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "No se pudo actualizar el evento."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/50 p-4"
      onMouseDown={onClose}
    >
      <div
        className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl"
        onMouseDown={(mouseEvent) => mouseEvent.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-event-title"
      >
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            <h3 id="edit-event-title" className="text-xl font-bold text-slate-800">
              Editar evento
            </h3>
            <p className="text-sm text-slate-500">ID #{event.idchampionship}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full px-3 py-1 text-2xl leading-none text-slate-500 hover:bg-slate-100"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <label className="text-sm font-medium text-slate-700 md:col-span-2">
            Nombre *
            <input
              name="name"
              value={form.name}
              onChange={handleChange}
              required
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-normal focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </label>

          <label className="text-sm font-medium text-slate-700">
            Periodo *
            <select
              name="periodo"
              value={form.periodo}
              onChange={handleChange}
              required
              className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 font-normal focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
            >
              <option value="">Seleccione</option>
              {periods.map((period) => (
                <option key={period} value={period}>
                  {period}
                </option>
              ))}
            </select>
          </label>

          <label className="text-sm font-medium text-slate-700">
            Estado
            <select
              name="state"
              value={form.state}
              onChange={handleChange}
              className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 font-normal focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
            >
              <option value="1">Por iniciar</option>
              <option value="2">En curso</option>
              <option value="3">Finalizado</option>
            </select>
          </label>

          <label className="text-sm font-medium text-slate-700">
            Fecha de inicio
            <input
              type="date"
              name="startdate"
              value={form.startdate}
              onChange={handleChange}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-normal focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </label>

          <label className="text-sm font-medium text-slate-700">
            Fecha de fin
            <input
              type="date"
              name="enddate"
              value={form.enddate}
              onChange={handleChange}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-normal focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </label>

          <label className="text-sm font-medium text-slate-700">
            Lugar *
            <input
              name="place"
              value={form.place}
              onChange={handleChange}
              required
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-normal focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </label>

          <label className="text-sm font-medium text-slate-700">
            Departamento
            <select
              name="iddepartamento"
              value={form.iddepartamento}
              onChange={handleChange}
              className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 font-normal focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
            >
              <option value="">Sin departamento</option>
              {departments.map((department) => (
                <option
                  key={department.iddepartamento}
                  value={department.iddepartamento}
                >
                  {department.name}
                </option>
              ))}
            </select>
          </label>

          <div className="md:col-span-2">
            <label className="text-sm font-medium text-slate-700">
              Reemplazar imagen
              <input
                type="file"
                accept=".jpg,.jpeg,.png,.webp,.gif,.avif,image/jpeg,image/png,image/webp,image/gif,image/avif"
                onChange={handleImageChange}
                className="mt-1 block w-full rounded-lg border border-slate-300 text-sm file:mr-4 file:border-0 file:bg-blue-50 file:px-4 file:py-2 file:font-semibold file:text-blue-700"
              />
            </label>
            {imagePreview && (
              <div className="mt-3 flex h-40 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
                <img
                  src={imagePreview}
                  alt="Vista previa del evento"
                  className="h-full w-full object-contain"
                />
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2 md:col-span-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded-lg bg-blue-600 px-5 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {saving ? "Guardando..." : "Guardar cambios"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EventEditModal;
